import type { Image } from '@/types/Image'
import type { DamImage } from '@/types/DamImage'
import { getDamImageSrc, getDamImageSrcSet } from '@/utils/image'
import {
  DAM_IMAGE_AUTOHEIGHT_HEIGHT_DEFAULT,
  DAM_IMAGE_AUTOHEIGHT_SRCSET_CONFIG,
  DAM_IMAGE_AUTOHEIGHT_WIDTH_DEFAULT,
  DAM_IMAGE_LIGHTBOX_HEIGHT_DEFAULT,
  DAM_IMAGE_LIGHTBOX_SRCSET_CONFIG,
  DAM_IMAGE_LIGHTBOX_WIDTH_DEFAULT,
  DAM_IMAGE_RATIO4x3_HEIGHT_DEFAULT,
  DAM_IMAGE_RATIO4x3_SRCSET_CONFIG,
  DAM_IMAGE_RATIO4x3_WIDTH_DEFAULT,
} from '@/config/image'
import type { GallerySetupConfig } from '@/types/GallerySetupConfig'

export function useImageFactory() {
  const createDefault = (): Image => {
    return {
      texts: {
        author: '',
        description: '',
      },
      dam: {
        damId: '',
        regionId: 0,
      },
      sizes: {
        autoHeight: {
          src: '',
          srcset: '',
          width: 0,
          height: 0,
        },
        ratio4x3: {
          src: '',
          srcset: '',
          width: 0,
          height: 0,
        },
        lightBox: {
          src: '',
          srcset: '',
          width: 0,
          height: 0,
        },
      },
    }
  }

  const createFromDamImage = (damImage: DamImage, setupConfig: GallerySetupConfig): Image => {
    const image = createDefault()
    if (setupConfig.imageAuthorPropertyKey === 'source' && !damImage.texts.author) {
      damImage.texts.author = damImage.texts.source ?? ''
    }

    image.texts.description = damImage.texts.description
    image.texts.author = damImage.texts.author
    image.dam.damId = damImage.dam.damId
    image.dam.regionId = damImage.dam.regionId

    // auto-height
    image.sizes.autoHeight.width = DAM_IMAGE_AUTOHEIGHT_WIDTH_DEFAULT
    image.sizes.autoHeight.height = DAM_IMAGE_AUTOHEIGHT_HEIGHT_DEFAULT
    image.sizes.autoHeight.src = getDamImageSrc(
      setupConfig.damBaseUrlImage,
      image,
      image.sizes.autoHeight.width,
      image.sizes.autoHeight.height
    )
    image.sizes.autoHeight.srcset = getDamImageSrcSet(
      setupConfig.damBaseUrlImage,
      image,
      setupConfig.sizeMap?.autoHeightSrcSet ?? DAM_IMAGE_AUTOHEIGHT_SRCSET_CONFIG
    )

    // ratio 4x3
    image.sizes.ratio4x3.width = setupConfig.sizeMap?.ratio4x3DefaultWidth ?? DAM_IMAGE_RATIO4x3_WIDTH_DEFAULT
    image.sizes.ratio4x3.height = setupConfig.sizeMap?.ratio4x3DefaultHeight ?? DAM_IMAGE_RATIO4x3_HEIGHT_DEFAULT
    image.sizes.ratio4x3.src = getDamImageSrc(
      setupConfig.damBaseUrlImage,
      image,
      image.sizes.ratio4x3.width,
      image.sizes.ratio4x3.height
    )
    image.sizes.ratio4x3.srcset = getDamImageSrcSet(
      setupConfig.damBaseUrlImage,
      image,
      setupConfig.sizeMap?.ratio4x3SrcSet ?? DAM_IMAGE_RATIO4x3_SRCSET_CONFIG
    )

    // lightbox size
    image.sizes.lightBox.width = DAM_IMAGE_LIGHTBOX_WIDTH_DEFAULT
    image.sizes.lightBox.height = DAM_IMAGE_LIGHTBOX_HEIGHT_DEFAULT
    image.sizes.lightBox.src = getDamImageSrc(
      setupConfig.damBaseUrlImage,
      image,
      image.sizes.lightBox.width,
      image.sizes.lightBox.height
    )
    image.sizes.lightBox.srcset = getDamImageSrcSet(
      setupConfig.damBaseUrlImage,
      image,
      DAM_IMAGE_LIGHTBOX_SRCSET_CONFIG
    )

    return image
  }

  return {
    createDefault,
    createFromDamImage,
  }
}
